import { firstQueryValue } from '@omnea-app/fe-routing/routeParams'
import { ROUTE_REGISTRY, type RouteDefinition, type RouteSegment } from './routeRegistry'
import type { RouteKey } from '@omnea-app/fe-routing/routes/routeKeys'
import type { ParsedUrlQuery } from 'querystring'

type Registry = typeof ROUTE_REGISTRY

/** A route addressable without caller-supplied values, so a link cannot name one that needs them. */
export type NavigableRouteKey = {
  [Key in RouteKey]: Registry[Key] extends { params: readonly string[] } ? never : Key
}[RouteKey]

type ParameterisedRouteKey = Exclude<RouteKey, NavigableRouteKey>

// The segment's param is filled from the registry, so the caller never supplies it.
type ParamsOf<Key extends RouteKey> = Registry[Key] extends { params: readonly (infer Param extends string)[] }
  ? Exclude<Param, Registry[Key] extends { segment: RouteSegment<infer SegmentParam> } ? SegmentParam : never>
  : never

/** An optional catch-all matches its own parent path, so the route is entered with the segment absent. */
const OPTIONAL_CATCH_ALL = /\/\[\[\.\.\..+\]\]$/

const DYNAMIC_SEGMENT = /^\[(\.\.\.)?(.+)\]$/

type DynamicParam = { name: string; isCatchAll: boolean }

const paramOf = (patternPart: string): DynamicParam | null => {
  const match = DYNAMIC_SEGMENT.exec(patternPart)
  const name = match?.[2]
  if (name == null) return null

  return { name, isCatchAll: match?.[1] != null }
}

const missingParamError = (key: RouteKey, paramName: string) =>
  new Error(`Route ${key} needs ${paramName}, which the address being left does not carry`)

const readParam = (key: RouteKey, paramName: string, query: ParsedUrlQuery): string => {
  const value = firstQueryValue(query, paramName)
  if (value == null) throw missingParamError(key, paramName)

  return value
}

const readCatchAll = (key: RouteKey, paramName: string, query: ParsedUrlQuery): string => {
  const catchAllValue = query[paramName]
  if (catchAllValue == null || catchAllValue.length === 0) throw missingParamError(key, paramName)

  return Array.isArray(catchAllValue) ? catchAllValue.join('/') : catchAllValue
}

/** The URL for a route, taking the values it needs from the address the caller is on. */
export const hrefForFrom = (key: RouteKey, query: ParsedUrlQuery): string => {
  const definition: RouteDefinition = ROUTE_REGISTRY[key]
  if (definition.href != null) return definition.href

  const { segment } = definition
  const parts = definition.pattern.replace(OPTIONAL_CATCH_ALL, '').split('/')
  const base = parts
    .map((part) => {
      const param = paramOf(part)
      if (param == null) return part
      if (segment?.queryParam === param.name) return segment.value

      return param.isCatchAll ? readCatchAll(key, param.name, query) : readParam(key, param.name, query)
    })
    .join('/')

  const patternCarriesSegment = parts.some((part) => paramOf(part)?.name === segment?.queryParam)

  return segment != null && patternCarriesSegment === false ? `${base}/${segment.value}` : base
}

export const hrefFor = (key: NavigableRouteKey): string => hrefForFrom(key, {})

/** Each value is encoded, so an id cannot add a path segment or a query. */
export const hrefWith = <Key extends ParameterisedRouteKey>(key: Key, params: Readonly<Record<ParamsOf<Key>, string>>): string =>
  hrefForFrom(key, Object.fromEntries(Object.entries<string>(params).map(([param, value]) => [param, encodeURIComponent(value)])))

// A catch-all spans a varying number of path parts, and a segment can be appended past the pattern, so a
// link is matched only against routes whose pattern alone fixes every part.
type FixedRouteKey = {
  [Key in ParameterisedRouteKey]: Registry[Key] extends { segment: RouteSegment }
    ? never
    : Registry[Key]['pattern'] extends `${string}[...${string}`
      ? never
      : Key
}[ParameterisedRouteKey]

/** Whether `href` opens the route with the given values; a value left out may be anything. */
export const isHrefOf = <Key extends FixedRouteKey>(
  key: Key,
  href: string,
  params: Partial<Readonly<Record<ParamsOf<Key>, string>>>
): boolean => {
  const definition: RouteDefinition = ROUTE_REGISTRY[key]
  const values: Readonly<Record<string, string | undefined>> = params
  const patternParts = definition.pattern.split('/')
  const hrefParts = (href.split(/[?#]/)[0] ?? '').split('/')

  return (
    hrefParts.length === patternParts.length &&
    patternParts.every((part, index) => {
      const param = paramOf(part)
      if (param == null) return part === hrefParts[index]

      const value = values[param.name]
      return value == null ? hrefParts[index] !== '' : hrefParts[index] === encodeURIComponent(value)
    })
  )
}
